import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import { useHistory } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useInfiniteScroll } from 'shared/hooks'
import {
	Button,
	Panel,
	Margin,
	Table,
	Text,
	ContentLoader,
} from 'shared/components'
import { profile } from 'routes/route.map'
import { BottomNavigation } from '../../public/events/overview/components/bottomNavigation'

import * as styles from './styles'

// Helper function to format date as "Month Day, Year at Time"
const getFormattedDate = (timestamp) => {
	if (!timestamp) return ''

	const date = new Date(timestamp)
	const dateOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	}
	const timeOptions = {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	}

	const formattedDate = date.toLocaleDateString('en-US', dateOptions)
	const formattedTime = date.toLocaleTimeString('en-US', timeOptions)

	return `${formattedDate} at ${formattedTime}`
}

const movementTypeLabels = {
	INWIN_PLACE: 'InWin Placed',
	INWIN_REFUND: 'InWin Refunded',
	TOPUP: 'Balance Topped Up',
}

const getMovementTypeLabel = (type) => movementTypeLabels[type] || type || 'Transaction'

const AccountComponent = ({
	appStore,
	columns,
	loadAccountMovements,
	onUserPayment,
}) => {
	const { loggedEntity } = appStore.auth
	const { isSponsor, isBeneficiary, wallet } = loggedEntity
	const [isLoadingPayment, setLoadingPayment] = useState(false)
	const [hasData, setHasData] = useState(false)
	const history = useHistory()

	// Infinite scrolling state
	const [movementsData, setMovementsData] = useState({ content: [], loading: false })
	const [pagination, setPagination] = useState({
		page: 0,
		hasMore: true,
		loadingMore: false
	})
	const paginationRef = useRef(pagination)

	// Keep pagination ref in sync
	useEffect(() => {
		paginationRef.current = pagination
	}, [pagination])

	const isShowPayButton = !isSponsor && !isBeneficiary

	const onClickPayButton = () => {
		onUserPayment()
		setLoadingPayment(true)
	}

	const handleBackClick = () => {
		history.push(profile.index())
	}

	const payButton = () => {
		return isLoadingPayment ? (
			<Margin tb={4} css={styles.wrapperLoader}>
				<ContentLoader loading={true} />
			</Margin>
		) : (
			<Margin top={3}>
				<Button primary onClick={onClickPayButton} css={{ width: '96%', height: '36px', fontWeight: 'bold', margin: '0 auto', display: 'block' }}>
					Top Up Balance
				</Button>
			</Margin>
		)
	}

	// Load initial data on mount
	useEffect(() => {
		const loadInitialData = async () => {
			setMovementsData(prev => ({ ...prev, loading: true }))
			setPagination(prev => ({ ...prev, page: 0, hasMore: true }))

			try {
				const result = await loadAccountMovements({
					page: 0,
					pageSize: 20
				})

				const content = result?.content || []
				setMovementsData({ content, loading: false })
				setHasData(content.length > 0)

				const hasMoreItems = content.length === 20

				setPagination(prev => ({
					...prev,
					hasMore: hasMoreItems
				}))
			} catch (error) {
				setMovementsData(prev => ({ ...prev, loading: false }))
			}
		}

		loadInitialData()
	}, [loadAccountMovements])

	// Load more data for infinite scrolling
	const loadMoreData = useCallback(async () => {
		const currentPagination = paginationRef.current

		if (currentPagination.loadingMore || !currentPagination.hasMore) {
			return
		}

		const nextPage = currentPagination.page + 1

		setPagination(prev => ({ ...prev, loadingMore: true }))

		try {
			const result = await loadAccountMovements({
				page: nextPage,
				pageSize: 20
			})

			const newContent = result?.content || []

			setMovementsData(prev => {
				const existingIds = new Set(prev.content.map(item => item.id))
				const uniqueNewContent = newContent.filter(item => !existingIds.has(item.id))

				return {
					...prev,
					content: [...prev.content, ...uniqueNewContent]
				}
			})

			const hasMoreItems = newContent.length === 20

			setPagination(prev => ({
				...prev,
				page: nextPage,
				hasMore: hasMoreItems,
				loadingMore: false
			}))
		} catch (error) {
			setPagination(prev => ({ ...prev, loadingMore: false }))
		}
	}, [loadAccountMovements])

	useInfiniteScroll({
		enabled: true,
		getPagination: () => paginationRef.current,
		getContentLength: () => movementsData.content.length,
		loadMoreData,
	})

	const loadData = async (...args) => {
		// This is for the Table component fallback (desktop)
		const result = await loadAccountMovements(...args)
		const hasContent = result.content?.length > 0
		setHasData(hasContent)
		return {
			...result,
			content: result.content || []
		}
	}

	return (
		<>
			{/* Mobile Header */}
			<div css={styles.mobileHeader}>
				<div css={styles.headerContainer}>
					<FontAwesomeIcon
						icon="arrow-left"
						css={styles.backArrow}
						onClick={handleBackClick}
					/>
					<div css={styles.titleContainer}>
						<Text size={16} bold css={styles.headerTitle}>
							Balance Management
						</Text>
					</div>
				</div>
			</div>

			<Panel css={styles.panel}>
				<div css={styles.contentWrapper}>
					<Margin bottom={5} css={styles.title}>
						<Text as="h1" size={18} bold>
							Balance Management
						</Text>
						{isShowPayButton && (
							<Text as="h2" size={11} css={styles.grey}>
								Balance: <Text size={11} css={styles.count}>${wallet.balance}</Text>
							</Text>
						)}
					</Margin>

					{isShowPayButton && (
						<>
							{/* Current Balance Card */}
							<div css={styles.currentBalanceCard}>
								<div css={styles.movementInfo}>
									<div css={styles.movementTypeWithLogo}>
										<img src="./img/inwin-tiny-white-logo.png" alt="InWin" css={styles.currentBalanceInwinLogo} />
										<Text size={14} bold css={styles.movementType}>
											Current Balance
										</Text>
									</div>
									<Text size={10} css={styles.movementDate}>
										{getFormattedDate(new Date())}
									</Text>
								</div>
								<div css={styles.movementAmount}>
									<Text size={18} bold css={styles.currentBalanceAmount}>
										${wallet.balance}
									</Text>
								</div>
							</div>

							{payButton()}
						</>
					)}

					<div css={styles.pageWrapper}>
						{/* Mobile infinite scroll view */}
						<ContentLoader loading={movementsData.loading}>
							<div css={styles.movementsContainer}>
								{movementsData.content.map((movement, index) => (
									<div key={movement.id || `movement-${index}`} css={styles.movementCard}>
						<div css={styles.movementInfo}>
							{movement.type === 'INWIN_PLACE' || movement.type === 'INWIN_REFUND' || movement.type === 'TOPUP' ? (
								<div css={styles.movementTypeWithLogo}>
									<img src="./img/inwin-tiny-white-logo.png" alt="InWin" css={styles.inwinLogo} />
									<Text size={12} bold css={styles.movementType}>
										{getMovementTypeLabel(movement.type)}
									</Text>
								</div>
							) : (
												<Text size={12} bold css={styles.movementType}>
													{movement.type || 'Transaction'}
												</Text>
											)}
											<Text size={10} css={styles.movementDate}>
												{getFormattedDate(movement.time)}
											</Text>
										</div>
										<div css={styles.movementAmount}>
											<Text size={12} bold css={movement.amount > 0 ? styles.positiveAmount : styles.negativeAmount}>
												${Math.abs(movement.amount || 0).toFixed(2)}
											</Text>
										</div>
									</div>
								))}

								{movementsData.content.length === 0 && !movementsData.loading && (
									<div css={styles.emptyState}>
										<Text size={14} css={styles.emptyText}>
											No transactions found
										</Text>
									</div>
								)}

								{pagination.loadingMore && (
									<div css={styles.loadingMore}>
										<ContentLoader loading={true}>
											<div css={styles.loadingPlaceholder}>
												<Text size={12} css={styles.loadingText}>
													Loading more transactions...
												</Text>
											</div>
										</ContentLoader>
									</div>
								)}
							</div>
						</ContentLoader>

						{/* Fallback Table for desktop (hidden on mobile) */}
						<div css={styles.desktopTable}>
							<Table
								id="account-movements"
								columns={columns}
								loadData={loadData}
								minRows={0}
								className="bordered"
								noDataText=""
								css={!hasData ? styles.emptyTable : undefined}
							/>
						</div>
					</div>
				</div>
			</Panel>

			<BottomNavigation />
		</>
	)
}

AccountComponent.propTypes = {
	appStore: PropTypes.shape().isRequired,
	currentBalance: PropTypes.string,
	columns: PropTypes.arrayOf(PropTypes.shape()),
	loadAccountMovements: PropTypes.func.isRequired,
	onUserPayment: PropTypes.func.isRequired,
}

export default observer(AccountComponent)
